/**
 * MiniRow — render one record using the AGENT_COLUMNS layout for
 * its source agent. Each cell labels its column (so the row is
 * readable in isolation, without the Step's full table header
 * context) and dispatches to a type-aware renderer:
 *
 *   id / ref       → monospace ID
 *   refs           → monospace chip strip
 *   chips          → humanised pill strip
 *   levelBadge     → LevelBadge with severityStyle colour
 *   categoryPill   → CategoryPill
 *   numeric        → tabular-nums plain text
 *   text/longText  → plain text
 *
 * Used by AddBody (state="added"), DeleteBody (state="removed",
 * including cascade rows), and BulkImportBody. UpdateBody renders a
 * field-by-field FieldDiff instead.
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge/LevelBadge';
import CategoryPill from '../../../../components/ui/CategoryPill/CategoryPill';
import { AGENT_COLUMNS, AgentColumn } from '../../utils/operationMeta';
import { resolveLevelColor } from '../../utils/severityResolver';
import styles from './MiniRow.module.css';

export type MiniRowState = 'added' | 'removed' | 'unchanged' | undefined;

interface MiniRowProps {
  agentName: string;
  record: Record<string, unknown> | null | undefined;
  state?: MiniRowState;
}

const Empty = () => <span className={styles.muted}>—</span>;

function renderCell(
  col: AgentColumn,
  value: unknown,
  agentName: string,
  state: MiniRowState,
): React.ReactNode {
  if (value === null || value === undefined || value === '') return <Empty />;

  switch (col.type) {
    case 'id':
    case 'ref':
      return <span className={styles.idMono}>{String(value)}</span>;

    case 'refs': {
      const arr = Array.isArray(value) ? value : [value];
      return arr.map((v) => (
        <span key={String(v)} className={`${styles.chip} ${styles.chipMono}`}>
          {String(v)}
        </span>
      ));
    }

    case 'chips': {
      const arr = Array.isArray(value) ? value : [value];
      return arr.map((v) => (
        <span key={String(v)} className={styles.chip}>
          {String(v)}
        </span>
      ));
    }

    case 'levelBadge': {
      const color = resolveLevelColor(agentName, col.key, value);
      return <LevelBadge color={color} label={String(value)} size="sm" />;
    }

    case 'categoryPill':
      return <CategoryPill label={String(value)} size="sm" />;

    case 'numeric':
      return <span className={styles.numCell}>{String(value)}</span>;

    case 'text':
    case 'longText':
    default:
      // Allow `state` to participate in striking through text-only cells
      // (parent .miniRowRemoved handles the line-through CSS).
      void state;
      return <span>{String(value)}</span>;
  }
}

export default function MiniRow({ agentName, record, state }: MiniRowProps) {
  const cols = AGENT_COLUMNS[agentName];
  if (!cols || !record) return null;

  const rowClass = [
    styles.miniRow,
    state === 'added' ? styles.miniRowAdded : '',
    state === 'removed' ? styles.miniRowRemoved : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass}>
      {cols.map((col) => (
        <div key={col.key} className={styles.miniCell} style={{ flex: col.flex }}>
          <div className={styles.miniCellLabel}>{col.label}</div>
          <div className={styles.miniCellValue}>
            {renderCell(col, record[col.key], agentName, state)}
          </div>
        </div>
      ))}
    </div>
  );
}
